/**
 * Smile Perfected attribution — one script for every lead surface:
 * ClickFunnels funnels (FB lead funnel, AACD buy funnel) AND the Shopify store
 * (Addify B2B request form, request-info/contact form, cart).
 *
 * Install:
 *  - ClickFunnels: paste into each funnel's Settings > Head Tracking Code in <script> tags.
 *  - Shopify: paste into theme.liquid before </head> in <script> tags (or a GTM Custom
 *    HTML tag on all store pages).
 *
 * What it does (issues #17/#21, docs/superpowers/specs/2026-07-31-fb-funnel-lead-attribution-design.md):
 *  1. First touch: reads utm_* / fbclid / fb_*_id / ad / tag from the query string, stores
 *     them in localStorage with a first-party cookie fallback (90 days). The cookie is set
 *     on the registrable domain (.smileperfected.com), so attribution captured on the main
 *     site or a funnel carries to shop.smileperfected.com and back. Existing values are
 *     never overwritten: attribution stays first-touch across every hop.
 *  2. Every page: fills (or creates) hidden inputs on every form, so submissions carry
 *     attribution (ClickFunnels webhook -> CRM; Shopify contact/Addify forms -> email and
 *     app records). On forms posting to Shopify's /contact, fields are named
 *     contact[utm_source] so Shopify includes them in the submission.
 *  3. Shopify only: writes attribution into cart attributes (/cart/update.js), so every
 *     order carries it as note_attributes readable by the Admin API sync (#12).
 *  4. Pushes a `sp_attribution` dataLayer event with the stored values plus a fresh
 *     sp_event_id, for GTM tags (Meta Pixel event_id dedupe, GA4 params).
 *  5. CAPI dedup (issue #17, docs/superpowers/specs/2026-08-11-capi-server-events-design.md):
 *     persists two per-conversion event ids (sp_eid_lead, sp_eid_purchase) that the GTM
 *     Lead/Purchase tags AND the CRM's server-side Conversions API events both carry, so
 *     Meta counts each conversion once. Beacon payloads carry the ids plus the _fbp/_fbc
 *     pixel cookies; funnel confirmation pages send a field-less "purchase ping" so the
 *     server fires Purchase only when payment actually completed.
 */
(function () {
  'use strict'

  var PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'fbclid', 'fb_campaign_id', 'fb_adset_id', 'fb_ad_id', 'ad', 'tag',
  ]
  var STORE_PREFIX = 'sp_attr_'
  var COOKIE_DAYS = 90

  function readQuery() {
    var out = {}
    try {
      var qs = new URLSearchParams(window.location.search)
      for (var i = 0; i < PARAMS.length; i++) {
        var v = qs.get(PARAMS[i])
        if (v) out[PARAMS[i]] = v.slice(0, 500)
      }
    } catch (e) { /* very old browser: skip */ }
    return out
  }

  function cookieDomain() {
    // .smileperfected.com whether on go., shop., or www., so every subdomain shares the
    // cookie; falls back to the bare host on localhost/previews.
    var parts = window.location.hostname.split('.')
    return parts.length >= 2 ? '.' + parts.slice(-2).join('.') : window.location.hostname
  }

  function setCookie(name, value) {
    try {
      var d = new Date()
      d.setTime(d.getTime() + COOKIE_DAYS * 864e5)
      document.cookie = name + '=' + encodeURIComponent(value) +
        ';expires=' + d.toUTCString() + ';path=/;domain=' + cookieDomain() + ';SameSite=Lax'
    } catch (e) { /* ignore */ }
  }

  function getCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
      return m ? decodeURIComponent(m[1]) : null
    } catch (e) { return null }
  }

  function getStored(key) {
    var v = null
    try { v = window.localStorage.getItem(STORE_PREFIX + key) } catch (e) { /* ignore */ }
    return v || getCookie(STORE_PREFIX + key)
  }

  function setStored(key, value) {
    try { window.localStorage.setItem(STORE_PREFIX + key, value) } catch (e) { /* ignore */ }
    setCookie(STORE_PREFIX + key, value)
  }

  // 1. Capture first touch (never overwrite an existing value).
  var fresh = readQuery()
  for (var key in fresh) {
    if (!getStored(key)) setStored(key, fresh[key])
  }
  if (!getStored('landing_url')) setStored('landing_url', window.location.href.slice(0, 1000))
  if (!getStored('referrer') && document.referrer) setStored('referrer', document.referrer.slice(0, 1000))

  // Collect everything we have (params + landing/referrer) for fill + cart + dataLayer.
  var ALL = PARAMS.concat(['landing_url', 'referrer'])
  var attribution = {}
  for (var j = 0; j < ALL.length; j++) {
    var stored = getStored(ALL[j])
    if (stored) attribution[ALL[j]] = stored
  }
  var hasAttribution = false
  for (var k0 in attribution) { hasAttribution = true; break }

  // Persisted per-conversion event ids (issue #17 CAPI dedup). Unlike sp_event_id (fresh
  // per pageview), these survive the submit page -> TY/confirmation hop, so the GTM tag
  // and the CRM's server event carry the SAME id and Meta dedupes them. Side effect: a
  // confirmation-page refresh re-fires Purchase with the same id, so the refresh dupe
  // now deduplicates too (v1's known limit).
  function newEid() {
    return 'sp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10)
  }
  function ensureEid(key) {
    var v = getStored(key)
    if (!v) {
      v = newEid()
      setStored(key, v)
    }
    return v
  }
  var EIDS = { lead: ensureEid('eid_lead'), purchase: ensureEid('eid_purchase') }
  // One purchase id per checkout, not per visitor (2026-08-11 fix: two purchases within
  // Meta's 48h dedup window shared an id and the second vanished). Lifecycle: the id is
  // "consumed" on the confirmation page (eid_purchase_done marker); the NEXT order-form
  // submit mints a fresh one. Confirmation refreshes and declined-card retries keep their
  // id, so both dedup cases still hold.
  function rotatePurchaseEidIfConsumed() {
    if (getStored('eid_purchase_done') === EIDS.purchase) {
      EIDS.purchase = newEid()
      setStored('eid_purchase', EIDS.purchase)
    }
  }

  // 2. Fill or create hidden inputs on every form so submissions carry attribution.
  // Shopify /contact forms need contact[...] field names to include the values in the
  // submission; everything else (ClickFunnels, Addify) gets the plain name.
  function fieldName(form, key) {
    var action = (form.getAttribute('action') || '').toLowerCase()
    return action.indexOf('/contact') !== -1 ? 'contact[' + key + ']' : key
  }

  function fillForms() {
    var forms = document.querySelectorAll('form')
    for (var f = 0; f < forms.length; f++) {
      // Never touch search or cart forms; the cart carries attribution via attributes below.
      var action = (forms[f].getAttribute('action') || '').toLowerCase()
      if (action.indexOf('/search') !== -1 || action.indexOf('/cart') !== -1) continue
      for (var k in attribution) {
        var input = forms[f].querySelector(
          'input[name="' + k + '"], input[name="contact[' + k + ']"]',
        )
        if (!input) {
          input = document.createElement('input')
          input.type = 'hidden'
          input.name = fieldName(forms[f], k)
          forms[f].appendChild(input)
        }
        if (!input.value) input.value = attribution[k]
      }
    }
  }

  // 3. Shopify storefront: persist attribution onto the cart, so the order carries it as
  // note_attributes for the Admin API sync (#12). Applied once per attribution set;
  // sessionStorage remembers what was written so repeat pageviews skip the request.
  function applyCartAttributes() {
    if (!window.Shopify || !hasAttribution || typeof window.fetch !== 'function') return
    var payload = {}
    for (var k in attribution) payload[k] = attribution[k]
    var fingerprint = ''
    try { fingerprint = JSON.stringify(payload) } catch (e) { return }
    try {
      if (window.sessionStorage.getItem(STORE_PREFIX + 'cart_applied') === fingerprint) return
    } catch (e) { /* ignore */ }
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: payload }),
      credentials: 'same-origin',
    }).then(function (res) {
      if (res.ok) {
        try { window.sessionStorage.setItem(STORE_PREFIX + 'cart_applied', fingerprint) } catch (e) { /* ignore */ }
      }
    }).catch(function () { /* offline/blocked: forms still carry attribution */ })
  }

  // Beacon (issue #21): ClickFunnels classic can no longer deliver server-side webhooks,
  // so on funnel pages every lead-form submission is also POSTed straight to the CRM from
  // the browser (navigator.sendBeacon, fire-and-forget, no preflight). Funnel host only:
  // Shopify submissions flow through their own adapters. Payment and password fields are
  // never collected.
  var BEACON_URL = 'https://crm.smileperfected.com/api/funnels/beacon'
  var BEACON_HOSTS = /(^|\.)go\.smileperfected\.com$/i
  // Buy/order pages (issue #21): submissions here are purchases awaiting manual QB entry,
  // not leads. Signaled by payment fields in the DOM or a known order-page path. CF slug
  // map (treacherous, keep in sync with funnels/README.md): aacd-special + aaca-special =
  // AACD buy page; deals-for-dentists = AACA buy page; order-form = FB funnel order.
  // Deliberately NOT a bare "order": CF auto-slugs like test-aaca-order are opt-in pages.
  var ORDER_PAGES = /aacd-special|aaca-special|aaca-gallerite|deals-for-dentists|order-?form|checkout|payment/i
  // Funnel order-confirmation pages (issue #17): a pageview here means payment completed,
  // so the CRM sends the server-side CAPI Purchase. Keep in sync with the GTM Purchase
  // triggers and purchaseValueForPage in crm/lib/meta-capi.ts.
  var CONFIRMATION_PAGES = /order-confirmation11261499|aacd-thank-you|offer-wall1670957945315/i

  function isSensitiveField(input) {
    if (input.type === 'password' || input.type === 'file') return true
    var sig = (input.name || '') + ' ' + (input.id || '') + ' ' + (input.getAttribute('autocomplete') || '')
    return /card|cc[-_ ]|cvv|cvc|exp|routing|account[-_ ]?number|ssn|billing/i.test(sig)
  }

  var SKIP_KEYS = /^(userevents|purchase|cf[-_]|webinar_delay|redirect|authenticity|utf8|time_zone|aff)/i

  function beaconForm(form) {
    // ClickFunnels puts the user-facing inputs OUTSIDE the <form> and mirrors them in at
    // submit time, so collect page-wide: form-less elements first (the user's own typing
    // wins over possibly-stale mirrors inside the form), then the form's own fields.
    var els = []
    var all = document.querySelectorAll('input, select, textarea')
    for (var p = 0; p < 2; p++) {
      for (var i = 0; i < all.length; i++) {
        var owner = all[i].form
        if ((p === 0 && !owner) || (p === 1 && owner === form)) els.push(all[i])
      }
    }
    var fields = {}
    var hasEmail = false
    var sawPayment = false
    for (var j = 0; j < els.length; j++) {
      var el = els[j]
      if (!el.name) continue
      if (isSensitiveField(el)) { if (el.type !== 'password' && el.type !== 'file') sawPayment = true; continue }
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) continue
      var v = (el.value || '').slice(0, 500)
      if (!v) continue
      // CF names fields like contact[email] or plain email; normalize to the bare key.
      var key = el.name.replace(/^contact\[/, '').replace(/\]$/, '').replace(/^additional_info\[/, '')
      if (SKIP_KEYS.test(key)) continue
      // Repeated keys (CF names every custom field custom_type): first wins, extras suffixed.
      if (!fields[key]) fields[key] = v
      else if (!fields[key + '_2']) fields[key + '_2'] = v
      else if (!fields[key + '_3']) fields[key + '_3'] = v
      if (/email/i.test(key) && v.indexOf('@') > 0) hasEmail = true
    }
    if (!hasEmail) return // not a lead form (or empty submit); nothing to send

    var isOrder = sawPayment || ORDER_PAGES.test(window.location.pathname)
    // A new checkout starts here: if the current purchase id was already consumed by a
    // completed purchase, mint a fresh one so this purchase counts on its own.
    if (isOrder) rotatePurchaseEidIfConsumed()
    sendToCrm(JSON.stringify({
      page: window.location.href.slice(0, 1000),
      order: isOrder,
      fields: fields,
      attribution: attribution,
      eids: EIDS,
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
    }))
  }

  function sendToCrm(payload) {
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(BEACON_URL, payload)) return
    } catch (e) { /* fall through */ }
    try {
      fetch(BEACON_URL, { method: 'POST', body: payload, keepalive: true, headers: { 'Content-Type': 'text/plain' } })
    } catch (e) { /* fire and forget */ }
  }

  function maybeBeacon(form) {
    if (!form || form.tagName !== 'FORM') return
    var action = (form.getAttribute('action') || '').toLowerCase()
    if (action.indexOf('/search') !== -1 || action.indexOf('/cart') !== -1) return
    // Several hooks can fire for one submission; send once per form per minute.
    var last = Number(form.getAttribute('data-sp-beaconed') || 0)
    if (last && Date.now() - last < 60000) return
    form.setAttribute('data-sp-beaconed', String(Date.now()))
    try { beaconForm(form) } catch (err) { /* never break the real submit */ }
  }

  if (BEACON_HOSTS.test(window.location.hostname)) {
    // Three hooks because ClickFunnels classic is hostile terrain: (1) the capture-phase
    // click on CF's #submit-form buttons is the one PROVEN to fire (2026-08-09 live test);
    // CF's own submission path emits no submit event, and something on the page stack
    // restores HTMLFormElement.prototype.submit to native after our head script runs.
    // (2) the submit event and (3) the prototype patch stay as generic fallbacks for
    // other page shapes. maybeBeacon dedupes if several fire together.
    document.addEventListener('click', function (e) {
      var t = e.target
      var a = t && t.closest ? t.closest('a[href="#submit-form"]') : null
      if (!a) return
      maybeBeacon(a.closest('form') || document.querySelector('form'))
    }, true)
    document.addEventListener('submit', function (e) { maybeBeacon(e.target) }, true)
    try {
      var nativeSubmit = HTMLFormElement.prototype.submit
      HTMLFormElement.prototype.submit = function () {
        maybeBeacon(this)
        return nativeSubmit.apply(this, arguments)
      }
    } catch (e) { /* locked-down environment: other hooks still work */ }

    // Purchase ping: field-less, fires once per confirmation pageview. The pixel cookies
    // give the server a match key even when the order-form beacon was lost; the shared
    // sp_eid_purchase lets Meta dedupe against the GTM Purchase tag (and a page refresh).
    if (CONFIRMATION_PAGES.test(window.location.pathname)) {
      // Mark the id consumed: the next order-form submit rotates it, while a refresh of
      // this page reuses it (same id -> Meta dedupes the refresh copy).
      if (getStored('eid_purchase_done') !== EIDS.purchase) setStored('eid_purchase_done', EIDS.purchase)
      sendToCrm(JSON.stringify({
        ping: 'purchase',
        page: window.location.href.slice(0, 1000),
        attribution: attribution,
        eids: EIDS,
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc'),
      }))
    }
  }

  function run() {
    fillForms()
    applyCartAttributes()
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run)
  } else {
    run()
  }
  // ClickFunnels re-renders forms late on some templates, and Shopify themes inject
  // drawers/popups after load; one delayed re-fill is cheap insurance.
  setTimeout(fillForms, 2000)

  // 4. dataLayer for GTM: attribution, a per-pageview event id (base PageView tag), and
  // the persisted conversion ids the Lead/Purchase tags share with the server (CAPI).
  var eventId = 'sp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10)
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'sp_attribution',
    sp_event_id: eventId,
    sp_eid_lead: EIDS.lead,
    sp_eid_purchase: EIDS.purchase,
    sp_attribution: attribution,
  })
})()
